import { ASSET_STATUS } from '@verantos-dev/types-projects';

import { useConceptGroupStatusByRef } from '../api/conceptGroupStatusQueries';
import type { EpiResourceRef } from '../types/epi-chat-types';

/**
 * Concept-group status. Reads the backend ``assetStatus`` off the
 * canonical per-CG detail query, keyed on the **real catalog id** carried in
 * ``hintArgs.conceptGroupId`` (minted at save, OR-6289) — NOT the card's
 * ``resourceId``, which is the ``cgpreview.<previewId>`` routing/slot key that the
 * canonical endpoint can't resolve. The endpoint derives the status by fetch
 * location: draft-fallback → Draft while unpublished, Snowflake → Ready once
 * approved. **No poll:** a CG has no async-worker step — the Draft → Ready flip is
 * a user publish action, caught by the ``useConfirmPreview`` invalidation (keyed
 * on the same real id). Falls back to Draft while loading, or when no real id is
 * present (stable-id workflow off). (CG has no Processing/Error asset-status.)
 *
 * ``defer`` (OR-6858): forwarded to ``useConceptGroupStatusByRef`` — true
 * while a sibling ``useBatchPrewarmConceptGroups`` batch request is in
 * flight, so this card doesn't fire its own request in the same render pass
 * the batch does. No new visual state needed: a deferred card simply stays
 * on the existing "Draft while loading" fallback a little longer.
 */
export const useConceptGroupStatusLabel = (ref: EpiResourceRef, defer = false): string => {
  const conceptGroupId =
    typeof ref.hintArgs?.conceptGroupId === 'string' ? ref.hintArgs.conceptGroupId : null;
  const { data } = useConceptGroupStatusByRef(conceptGroupId, ref.projectId, defer);
  return data?.assetStatus ?? ASSET_STATUS.DRAFT;
};
